import { useEffect, useState } from "react";
import {
  ErrorNotification,
  SuccessNotification,
} from "../tosterNotification/tosters";
import {
  Loader2,
  Users,
  UserX,
  Star,
  Zap,
  Coffee,
  ShieldCheck,
  Badge,
} from "lucide-react";
import axios from "axios";
import API_URL from "../component/config";
import { useNavigate } from "react-router-dom";
import Chart from "react-apexcharts";

export default function Dashboard() {
  const [loading, setLoading] = useState(false);
  const [userData, setUserData] = useState(null);
  const [chartSeries, setChartSeries] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    if (!userData?.experienceLevels) return;
    console.log("Updating chart series with:", userData.experienceLevels);
    setChartSeries([
      {
        name: "Profiles",
        data: [
          Number(userData.experienceLevels.fresher?.count || 0),
          Number(userData.experienceLevels.professional?.count || 0),
          Number(userData.experienceLevels.career_break?.count || 0),
          Number(userData.experienceLevels.freelancer?.count || 0),
        ],
      },
    ]);
  }, [userData]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.post(`${API_URL}/api/auth/get_count`);
      if (response.data.success) {
        setUserData(response.data.data);
        SuccessNotification("Dashboard data synced!");
      }
    } catch (error) {
      ErrorNotification("Failed to fetch dashboard stats.");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex flex-column justify-content-center align-items-center vh-100 bg-light">
        <Loader2 className="spinner-icon" size={60} color="#3b82f6" />
        <p className="mt-3 text-secondary fw-bold">Loading Stats...</p>
        <style jsx>{`
          .spinner-icon {
            animation: spin 1s linear infinite;
          }
          @keyframes spin {
            100% {
              transform: rotate(360deg);
            }
          }
        `}</style>
      </div>
    );
  }

  const statCards = [
    {
      label: "Total Users",
      value: userData?.totalUsers ?? 0,
      icon: <Users />,
      color: "primary",
      key: "ALL",
    },
    {
      label: "Expired Users",
      value: userData?.expiredUsers ?? 0,
      icon: <UserX />,
      color: "danger",
      key: "EXPIRED",
    },
    {
      label: "Custom Plan",
      value: userData?.paidUsersByPlan?.custom ?? 0,
      icon: <ShieldCheck />,
      color: "info",
      key: "CUSTOM",
    },
    {
      label: "Special Plan",
      value: userData?.paidUsersByPlan?.special ?? 0,
      icon: <Badge />,
      color: "info",
      key: "SPECIAL",
    },
    {
      label: "Free Plan",
      value: userData?.paidUsersByPlan?.free ?? 0,
      icon: <Coffee />,
      color: "secondary",
      key: "FREE",
    },
    {
      label: "Lean Plan",
      value: userData?.paidUsersByPlan?.lean ?? 0,
      icon: <Zap />,
      color: "warning",
      key: "LEAN",
    },
    {
      label: "Leap Plan",
      value: userData?.paidUsersByPlan?.leap ?? 0,
      icon: <Star />,
      color: "success",
      key: "LEAP",
    },
  ];

  const chartOptions = {
    chart: {
      type: "line",
      height: 500,
      toolbar: { show: false },
      animations: {
        enabled: true,
        speed: 800,
        animateGradually: {
          enabled: true,
          delay: 150,
        },
        dynamicAnimation: {
          enabled: true,
          speed: 350,
        },
      },
    },

    stroke: {
      curve: "smooth",
      width: 5,
    },

    markers: {
      size: 7,
    },

    tooltip: {
      theme: "dark",
    },

    xaxis: {
      categories: ["Fresher", "Professional", "Career Break", "Freelancer"],
      labels: {
        style: {
          colors: "#000",
        },
      },
    },

    yaxis: {
      labels: {
        style: {
          colors: "#000",
        },
      },
    },

    title: {
      text: "Profiles",
      align: "left",
      style: {
        color: "#000",
      },
    },
  };

  return (
    <div className="main-wrapper min-vh-100 text-capitalize bg-white">
      <h2 className="mb-4 fw-bold" style={{ color: "#6600cc" }}>
        Admin Dashboard
      </h2>

      {userData ? (
        <div className="row g-4 mb-4">
          {statCards.map((card, index) => (
            <div
              key={index}
              className="col-12 col-md-3"
              onClick={() =>
                navigate("/users-list", { state: { label: card.key } })
              }
            >
              <div className="stat-card border-0 shadow-sm card p-3">
                <div
                  className={`icon-box bg-${card.color}-subtle text-${card.color} mb-3`}
                >
                  {card.icon}
                </div>
                <span className="text-black small fw-medium">{card.label}</span>
                <h3 className="fw-bold text-black mt-1 mb-0">{card.value}</h3>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center p-5 card shadow-sm border-0">
          <h4 className="text-muted">No Dashboard Data Found</h4>
        </div>
      )}

      <div className="row">
        <div className="col-md-8 col-12">
          <div className="card p-3 shadow-sm w-100 rounded-4 border-0">
            {chartSeries.length > 0 && (
              <Chart
                options={chartOptions}
                series={chartSeries}
                type="line"
                height={300}
              />
            )}
          </div>
        </div>
        <div className="col-md-4 col-12">
          <div className="card p-2">
            <span className="card-title text-black fw-bold text-capitalize mb-n2">
              critical errors
            </span>
            <hr />
            <div className="card-body"></div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .stat-card {
          transition: all 0.3s ease;
          border-radius: 12px;
          cursor: pointer;
        }
        .stat-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
        }
        .icon-box {
          width: 45px;
          height: 45px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 10px;
        }
        .bg-primary-subtle {
          background-color: #cfe2ff;
        }
      `}</style>
    </div>
  );
}
